import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cross, LogIn, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Login failed. Check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-forest-deep px-4">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-96 w-96 -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative w-full max-w-md">
        <div className="rounded-[2rem] border border-cream/10 bg-cream p-10 shadow-2xl" data-testid="admin-login-card">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-forest text-cream">
              <Cross className="h-6 w-6" />
            </span>
            <h1 className="mt-5 font-serif text-3xl font-bold text-forest-deep">Admin Portal</h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-terracotta-dark">
              St. Mary's Charitable Trust
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-2 h-11"
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <div className="relative">
                <Input
                  id="admin-password"
                  type={showPw ? "text" : "password"}
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-2 h-11 pr-11"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  data-testid="admin-password-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 mt-1 -translate-y-1/2 text-stone-400 transition-colors hover:text-forest"
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} data-testid="admin-login-submit-button" className="w-full rounded-full bg-forest py-6 font-display text-cream hover:bg-forest-deep disabled:opacity-60">
              {loading ? "Signing in..." : (<>Sign In <LogIn className="ml-2 h-4 w-4" /></>)}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center">
          <Link to="/" data-testid="admin-back-home-link" className="text-sm text-cream/60 hover:text-gold">
            ← Back to website
          </Link>
        </p>
      </div>
    </div>
  );
}
